<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ReportHeader from '../../settings/reports/components/ReportHeader.vue';
import ReportFilterSelector from '../../settings/reports/components/FilterSelector.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import GmAnalyticsAPI from 'dashboard/api/gmAnalytics';

const { t } = useI18n();
const store = useStore();

// Reactive data
const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const channelId = ref(null);
const agentId = ref(null);
const isExporting = ref(false);
const exportProgress = ref(0);

// Initialize default date range (last 7 days)
onMounted(() => {
  const today = new Date();
  // Set to end of today to include all of today's conversations
  today.setHours(23, 59, 59, 999);
  
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6); // 6 days ago + today = 7 days
  sevenDaysAgo.setHours(0, 0, 0, 0); // Start of day
  
  from.value = Math.floor(sevenDaysAgo.getTime() / 1000);
  to.value = Math.floor(today.getTime() / 1000);
  
  console.log('🔧 GmExportConversations: Initialized date range:', {
    from: new Date(from.value * 1000).toISOString().split('T')[0],
    to: new Date(to.value * 1000).toISOString().split('T')[0],
    fromTimestamp: from.value,
    toTimestamp: to.value
  });
});

const onFilterChange = (updatedFilter) => {
  console.log('🔧 GmExportConversations: Filter change received:', updatedFilter);
  
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  channelId.value = updatedFilter.selectedInbox ? updatedFilter.selectedInbox.id : null;
  agentId.value = updatedFilter.selectedAgents && updatedFilter.selectedAgents.length > 0 ? updatedFilter.selectedAgents[0].id : null;
  
  console.log('🔧 GmExportConversations: Updated filters:', {
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
    channelId: channelId.value,
    agentId: agentId.value
  });
};

const exportRequests = ref([]);

// Load export requests on mount
onMounted(async () => {
  await loadExportRequests();
});

const loadExportRequests = async () => {
  try {
    const response = await GmAnalyticsAPI.getExportRequests();
    exportRequests.value = response.data.export_requests;
  } catch (error) {
    console.error('❌ Failed to load export requests:', error);
  }
};

const onExportClick = async () => {
  if (isExporting.value) return;
  
  try {
    isExporting.value = true;
    
    console.log('🚀 GmExportConversations: Starting export with filters:', {
      from: from.value,
      to: to.value,
      businessHours: businessHours.value,
      channelId: channelId.value,
      agentId: agentId.value
    });
    
    // Build export parameters
    const exportParams = {
      from: from.value,
      to: to.value,
      businessHours: businessHours.value,
      channelId: channelId.value,
      agentId: agentId.value
    };
    
    // Call the export API (now creates a request and queues job)
    const response = await GmAnalyticsAPI.exportConversations(exportParams);
    
    console.log('✅ GmExportConversations: Export request created:', response.data);
    
    // Reload export requests to show the new one
    await loadExportRequests();
    
  } catch (error) {
    console.error('❌ GmExportConversations: Export failed:', error);
    // TODO: Show error message to user
  } finally {
    isExporting.value = false;
  }
};

const downloadExport = async (request) => {
  try {
    const response = await GmAnalyticsAPI.downloadExport(request.id);
    
    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = request.filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('❌ Download failed:', error);
  }
};

const deleteExport = async (request) => {
  if (!confirm('Are you sure you want to delete this export?')) return;
  
  try {
    await GmAnalyticsAPI.deleteExportRequest(request.id);
    await loadExportRequests(); // Reload the list
  } catch (error) {
    console.error('❌ Delete failed:', error);
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString();
};

const formatRequestDateRange = (filters) => {
  if (filters.since && filters.until) {
    const fromDate = new Date(parseInt(filters.since) * 1000).toISOString().split('T')[0];
    const toDate = new Date(parseInt(filters.until) * 1000).toISOString().split('T')[0];
    return `${fromDate} to ${toDate}`;
  }
  return 'All time';
};

</script>

<template>
  <div class="export-conversations-page">
    <ReportHeader
      :header-title="$t('GM_ANALYTICS.EXPORT_CONVERSATIONS.HEADER')"
      :header-description="$t('GM_ANALYTICS.EXPORT_CONVERSATIONS.DESCRIPTION')"
    >
      <V4Button
        :label="$t('GM_ANALYTICS.EXPORT_CONVERSATIONS.EXPORT_BUTTON')"
        icon="i-ph-download-simple"
        size="sm"
        :loading="isExporting"
        :disabled="isExporting"
        @click="onExportClick"
      />
    </ReportHeader>

    <!-- Export Progress Bar -->
    <div v-if="isExporting" class="mb-6">
      <div class="bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.EXPORTING') }}
          </span>
          <span class="text-sm text-slate-500 dark:text-slate-400">
            {{ exportProgress }}%
          </span>
        </div>
        <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
          <div 
            class="bg-woot-500 h-2 rounded-full transition-all duration-300"
            :style="{ width: `${exportProgress}%` }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
        {{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.FILTER_SECTION_TITLE') }}
      </h3>
      
      <ReportFilterSelector
        :from="from"
        :to="to"
        :business-hours="businessHours"
        :show-inbox-filter="true"
        :show-agents-filter="true"
        @filter-change="onFilterChange"
      />
    </div>

    <!-- Export Information -->
    <div class="bg-slate-50 dark:bg-slate-900 rounded-lg p-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
        {{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.INFO_SECTION_TITLE') }}
      </h3>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600 dark:text-slate-400">
        <div>
          <strong>{{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.DATE_RANGE') }}:</strong>
          {{ new Date(from * 1000).toISOString().split('T')[0] }} - {{ new Date(to * 1000).toISOString().split('T')[0] }}
        </div>
        <div>
          <strong>{{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.BUSINESS_HOURS') }}:</strong>
          {{ businessHours ? $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.ENABLED') : $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.DISABLED') }}
        </div>
        <div v-if="channelId">
          <strong>{{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.CHANNEL') }}:</strong>
          {{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.FILTERED') }}
        </div>
        <div v-if="agentId">
          <strong>{{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.AGENT') }}:</strong>
          {{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.FILTERED') }}
        </div>
      </div>
      
      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p class="text-sm text-blue-700 dark:text-blue-300">
          <strong>{{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.NOTE') }}:</strong>
          {{ $t('GM_ANALYTICS.EXPORT_CONVERSATIONS.NOTE_TEXT') }}
        </p>
      </div>
    </div>

    <!-- Export Requests Table -->
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6 mt-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
        Recent Export Requests
      </h3>
      
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead class="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Date Range
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                File Size
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Records
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Created
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
            <tr v-for="request in exportRequests" :key="request.id" class="hover:bg-slate-50 dark:hover:bg-slate-700">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                {{ formatRequestDateRange(request.filters) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  request.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  request.status === 'processing' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                  request.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                  'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200'
                }`">
                  {{ request.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                {{ request.file_size_mb }} MB
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                {{ request.total_records || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                {{ formatDate(request.created_at) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  v-if="request.status === 'completed'"
                  @click="downloadExport(request)"
                  class="text-woot-600 hover:text-woot-900 dark:text-woot-400 dark:hover:text-woot-300 mr-3"
                >
                  Download
                </button>
                <button
                  @click="deleteExport(request)"
                  class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                >
                  Delete
                </button>
              </td>
            </tr>
            <tr v-if="exportRequests.length === 0">
              <td colspan="6" class="px-6 py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                No export requests found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.export-conversations-page {
  @apply p-6;
}
</style>
